import Vue from 'vue'
import Vuex from 'vuex'
import { setToken, setUser, removeToken, removeUser, getToken, getUser } from '@/utils/auth'

const silentError = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[Store]', ...args.map(a => a?.msg || a?.message || a))
  }
}

// 导入模块
import user from './modules/user'
import resource from './modules/resource'
import model from './modules/model'

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    user,
    resource,
    model
  },
  // 根状态
  state: {
    // 认证状态
    auth: {
      token: getToken() || '',
      user: getUser() || null,
      isAuthenticated: !!getToken()
    },
    
    // 用户管理
    users: [],
    totalUsers: 0,
    roles: [],
    
    // 用户分组
    userGroups: [],
    
    // 硬件设备管理
    devices: [],
    
    // 设备操作日志
    deviceLogs: [],
    
    // 系统管理
    system: {
      // 系统日志
      logs: [],
      
      // 通知模板
      notificationTemplates: []
    }
  },
  
  // 根状态的getters
  getters: {
    // 认证相关getters
    isAuthenticated: state => state.auth.isAuthenticated,
    currentUser: state => state.auth.user,
    authToken: state => state.auth.token,
    
    // 用户管理相关getters
    allUsers: state => state.users,
    userById: state => id => state.users.find(user => String(user.userid) === String(id)),
    allRoles: state => state.roles,
    roleById: state => id => state.roles.find(role => String(role.id) === String(id)),
    allUserGroups: state => state.userGroups,
    userGroupById: state => id => state.userGroups.find(group => String(group.id) === String(id)),
    
    // 硬件设备管理相关getters
    allDevices: state => state.devices,
    deviceById: state => id => state.devices.find(device => String(device.id) === String(id)),
    devicesByUserId: state => userId => state.devices.filter(device => String(device.user_id) === String(userId)),
    // 动态计算用户设备数量（用于数据一致性校验）
    userDeviceCount: (state, getters) => userId => {
      const user = getters.userById(userId)
      // 优先使用 bound_device_ids 计算，如果不存在则遍历 devices 表
      if (user && user.bound_device_ids && user.bound_device_ids.length > 0) {
        return user.bound_device_ids.length
      }
      return state.devices.filter(device => String(device.user_id) === String(userId)).length
    },
    deviceLogsByDeviceId: state => deviceId => state.deviceLogs.filter(log => String(log.device_id) === String(deviceId)),
    
    // 系统管理相关getters
    allSystemLogs: state => state.system.logs,
    systemLogsByType: state => type => state.system.logs.filter(log => log.type === type),
    allNotificationTemplates: state => state.system.notificationTemplates,
    notificationTemplateById: state => id => state.system.notificationTemplates.find(template => String(template.id) === String(id))
  },
  
  // 根状态的mutations
  mutations: {
    // 认证相关mutations
    SET_TOKEN(state, payload) {
      // payload 可以是 token 字符串，也可以是 { token, expire } 对象
      const token = typeof payload === 'string' ? payload : (payload.token || '')
      const expire = typeof payload === 'string' ? null : payload.expire
      
      state.auth.token = token
      if (token) {
        setToken(token, expire || 1)
        localStorage.setItem('token', token)
        if (expire) {
          const expireTimestamp = Date.now() + expire * 1000
          localStorage.setItem('tokenExpire', expireTimestamp.toString())
        }
      } else {
        removeToken()
        localStorage.removeItem('token')
        localStorage.removeItem('tokenExpire')
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('tokenExpire')
      }
      state.auth.isAuthenticated = !!token
    },
    SET_USER(state, user) {
      state.auth.user = user
      if (user) {
        setUser(user)
      } else {
        removeUser()
      }
    },
    LOGOUT(state) {
      state.auth.token = ''
      state.auth.user = null
      state.auth.isAuthenticated = false
      removeToken()
      removeUser()
      localStorage.removeItem('token')
      localStorage.removeItem('tokenExpire')
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('tokenExpire')
    },
    
    // 用户管理相关mutations
    SET_USERS(state, { list, total }) {
      state.users = list
      state.totalUsers = total
    },
    SET_ROLES(state, roles) {
      state.roles = roles
    },
    ADD_USER(state, user) {
      state.users.push(user)
    },
    UPDATE_USER(state, updatedUser) {
      const index = state.users.findIndex(user => String(user.userid) === String(updatedUser.userid))
      if (index !== -1) {
        state.users.splice(index, 1, { ...state.users[index], ...updatedUser })
      }
    },
    MERGE_USERS(state, newUsers) {
      if (!Array.isArray(newUsers)) return
      newUsers.forEach(newUser => {
        const index = state.users.findIndex(user => String(user.userid) === String(newUser.userid))
        if (index !== -1) {
          state.users.splice(index, 1, { ...state.users[index], ...newUser })
        } else {
          state.users.push(newUser)
        }
      })
    },
    DELETE_USER(state, userId) {
      const user = state.users.find(u => String(u.userid) === String(userId))
      if (user) {
        user.status = 0
      }
    },
    TOGGLE_USER_STATUS(state, userId) {
      const user = state.users.find(u => String(u.userid) === String(userId))
      if (user) {
        user.status = user.status === 1 ? 0 : 1
      }
    },
    SET_USER_STATUS(state, { userId, status }) {
      // 更新用户列表中的状态
      const user = state.users.find(u => String(u.userid) === String(userId))
      if (user) {
        user.status = status
      }
      
      // 更新用户分组中的状态
      state.userGroups.forEach(group => {
        if (group.users && Array.isArray(group.users)) {
          const groupUser = group.users.find(u => String(u.userid || u.userId || u.id) === String(userId))
          if (groupUser) {
            groupUser.status = status
          }
        }
      })
    },
    
    // 用户分组相关mutations
    SET_USER_GROUPS(state, groups) {
      state.userGroups = groups
    },
    ADD_USER_GROUP(state, group) {
      state.userGroups.push(group)
    },
    UPDATE_USER_GROUP(state, updatedGroup) {
      const index = state.userGroups.findIndex(group => String(group.id) === String(updatedGroup.id))
      if (index !== -1) {
        state.userGroups.splice(index, 1, { ...state.userGroups[index], ...updatedGroup })
      }
    },
    DELETE_USER_GROUP(state, groupId) {
      state.userGroups = state.userGroups.filter(group => group.id !== groupId)
    },
    
    // 硬件设备管理相关mutations
    SET_DEVICES(state, devices) {
      state.devices = devices
    },
    ADD_DEVICE(state, device) {
      state.devices.push(device)
    },
    UPDATE_DEVICE(state, updatedDevice) {
      const index = state.devices.findIndex(device => device.id === updatedDevice.id)
      if (index !== -1) {
        state.devices.splice(index, 1, updatedDevice)
      }
    },
    DELETE_DEVICE(state, deviceId) {
      state.devices = state.devices.filter(device => device.id !== deviceId)
    },
    BIND_DEVICE(state, { deviceId, userId }) {
      const device = state.devices.find(device => String(device.id) === String(deviceId))
      if (device) {
        const oldUserId = device.user_id
        device.user_id = userId
        
        // 更新旧用户的 bound_device_ids 和 deviceCount
        if (oldUserId) {
          const oldUser = state.users.find(u => String(u.userid) === String(oldUserId))
          if (oldUser) {
            if (oldUser.bound_device_ids) {
              oldUser.bound_device_ids = oldUser.bound_device_ids.filter(id => String(id) !== String(deviceId))
            }
            if (oldUser.deviceCount > 0) {
              oldUser.deviceCount--
            }
          }
        }
        
        // 更新新用户的 bound_device_ids 和 deviceCount
        const newUser = state.users.find(u => String(u.userid) === String(userId))
        if (newUser) {
          if (!newUser.bound_device_ids) {
            newUser.bound_device_ids = []
          }
          if (!newUser.bound_device_ids.some(id => String(id) === String(deviceId))) {
            newUser.bound_device_ids.push(deviceId)
            newUser.deviceCount = (newUser.deviceCount || 0) + 1
          }
        }
      }
    },
    UNBIND_DEVICE(state, deviceId) {
      const device = state.devices.find(device => String(device.id) === String(deviceId))
      if (device) {
        const userId = device.user_id || device.userId
        device.user_id = null
        device.userId = null
        
        // 同步更新用户的 bound_device_ids 和 deviceCount
        if (userId) {
          const user = state.users.find(u => String(u.userid) === String(userId))
          if (user) {
            if (user.bound_device_ids) {
              user.bound_device_ids = user.bound_device_ids.filter(id => String(id) !== String(deviceId))
            }
            if (user.deviceCount > 0) {
              user.deviceCount--
            }
          }
        }
      }
    },
    UPDATE_DEVICE_STATUS(state, { deviceId, status }) {
      const device = state.devices.find(device => device.id === deviceId)
      if (device) {
        device.status = status
      }
    },
    
    // 设备操作日志相关mutations
    ADD_DEVICE_LOG(state, log) {
      state.deviceLogs.push(log)
      // 限制最大 500 条，防止内存泄漏
      if (state.deviceLogs.length > 500) {
        state.deviceLogs = state.deviceLogs.slice(-500)
      }
    },

    // 系统管理相关mutations
    ADD_SYSTEM_LOG(state, log) {
      state.system.logs.push(log)
      // 限制最大 500 条，防止内存泄漏
      if (state.system.logs.length > 500) {
        state.system.logs = state.system.logs.slice(-500)
      }
    },
    ADD_NOTIFICATION_TEMPLATE(state, template) {
      state.system.notificationTemplates.push(template)
    },
    UPDATE_NOTIFICATION_TEMPLATE(state, updatedTemplate) {
      const index = state.system.notificationTemplates.findIndex(template => template.id === updatedTemplate.id)
      if (index !== -1) {
        state.system.notificationTemplates.splice(index, 1, updatedTemplate)
      }
    },
    DELETE_NOTIFICATION_TEMPLATE(state, templateId) {
      state.system.notificationTemplates = state.system.notificationTemplates.filter(template => template.id !== templateId)
    },
    TOGGLE_NOTIFICATION_TEMPLATE(state, templateId) {
      const template = state.system.notificationTemplates.find(t => t.id === templateId)
      if (template) {
        template.enabled = !template.enabled
      }
    }
  },
  
  // 根状态的actions
  actions: {
    // 获取所有用户
    async fetchUsers({ commit }, params) {
      try {
        if(process.env.NODE_ENV==="development")console.debug('Fetching users with params:', params)
        const response = await import('@/apis/user/user.js').then(m => m.getUserList(params))
        if(process.env.NODE_ENV==="development")console.debug('Raw user list response:', JSON.stringify(response))
        
        // request.js 返回的是 response.data，即 PageData 对象
        if (response) {
          // 后端返回的 PageData 结构包含 list 和 total
          // 兼容不同可能的响应格式
          let backendUsers = []
          let total = 0

          if (Array.isArray(response)) {
            backendUsers = response
            total = response.length
          } else if (response && response.list) {
            backendUsers = response.list
            total = response.total !== undefined ? response.total : (response.totalCount !== undefined ? response.totalCount : backendUsers.length)
          } else if (response && response.records) {
            backendUsers = response.records
            total = response.total !== undefined ? response.total : (response.totalCount !== undefined ? response.totalCount : backendUsers.length)
          } else if (response) {
            // 如果 response 既不是数组也没有 list/records，可能 response 本身就是数据对象
            backendUsers = [response]
            total = 1
          }
          
          if(process.env.NODE_ENV==="development")console.debug('Extracted backend users:', backendUsers)
          
          const users = backendUsers.map(u => ({
            userid: String(u.id || u.userid || ''),
            username: u.username || '',
            name: u.name || '',
            avatar: u.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
            email: u.email || '',
            mobile: u.mobile || '',
            roleId: u.roleId,
            gender: u.gender !== undefined ? u.gender : 0,
            status: u.status !== undefined ? (typeof u.status === 'number' ? u.status : (u.status === 'active' ? 1 : 0)) : 1,
            userLevel: u.userLevel || '普通',
            accountBalance: u.accountBalance || 0,
            points: u.points || 0,
            createDate: u.createDate || ''
          }))
          
          if(process.env.NODE_ENV==="development")console.debug('Mapped users for store:', users)
          commit('SET_USERS', { list: users, total: total })
          return Promise.resolve(response)
        } else {
          console.warn('获取用户列表失败: 响应为空')
          commit('SET_USERS', { list: [], total: 0 })
          return Promise.resolve({ list: [], total: 0 })
        }
      } catch (error) {
        silentError('获取用户列表异常:', error)
        commit('SET_USERS', { list: [], total: 0 })
        throw error
      }
    },
    
    // 获取所有角色
    async fetchRoles({ commit }) {
      try {
        const response = await import('@/apis/user/role.js').then(m => m.getRoleList())
        if (response) {
          // 统一角色数据字段名称，严格遵循后端命名: id, roleName
          const roles = response.map(r => ({
            id: r.id || '',
            roleName: r.roleName || '',
            roleCode: r.roleCode || '',
            remark: r.remark || '',
            status: r.status !== undefined ? r.status : 1,
            userCount: r.userCount || 0,
            menuIdList: r.menuIdList || [],
            dataScope: r.dataScope,
            buttonPermissions: r.buttonPermissions || []
          }))
          commit('SET_ROLES', roles)
          return Promise.resolve(roles)
        }
      } catch (error) {
        silentError('获取角色列表失败:', error)
        throw error
      }
    },
    
    // 创建角色
    async createRole({ dispatch }, roleData) {
      try {
        const response = await import('@/apis/user/role.js').then(m => m.saveRole(roleData))
        await dispatch('fetchRoles')
        return response
      } catch (error) {
        silentError('创建角色失败:', error)
        throw error
      }
    },
    
    // 更新角色
    async updateRole({ dispatch }, roleData) {
      try {
        const response = await import('@/apis/user/role.js').then(m => m.updateRole(roleData))
        await dispatch('fetchRoles')
        return response
      } catch (error) {
        silentError('更新角色失败:', error)
        throw error
      }
    },
    
    // 更新角色权限 (专门用于权限分配页面的保存)
    async updateRolePermissions({ dispatch }, roleData) {
      try {
        const response = await import('@/apis/user/role.js').then(m => m.updateRole(roleData))
        // 成功后刷新角色列表
        await dispatch('fetchRoles')
        return response
      } catch (error) {
        silentError('更新角色权限失败:', error)
        throw error
      }
    },
    
    // 删除角色
    async deleteRole({ dispatch }, roleIds) {
      try {
        const ids = Array.isArray(roleIds) ? roleIds : [roleIds]
        const response = await import('@/apis/user/role.js').then(m => m.deleteRole(ids))
        await dispatch('fetchRoles')
        return response
      } catch (error) {
        silentError('删除角色失败:', error)
        throw error
      }
    },
    
    // 获取指定用户详情
    async fetchUserById({ commit }, userId) {
      try {
        const response = await import('@/apis/user/user.js').then(m => m.getUserDetail(userId))
        if (response) {
          const user = {
            userid: response.userId || response.userid || response.id || userId,
            username: response.username || '',
            name: response.name || response.username || '',
            userLevel: response.userLevel || '普通',
            accountBalance: response.accountBalance || 0,
            points: response.points || 0,
            email: response.email || '',
            mobile: response.mobile || '',
            gender: response.gender !== undefined ? response.gender : 0,
            status: response.status !== undefined ? (typeof response.status === 'number' ? response.status : (response.status === 'active' ? 1 : 0)) : 1,
            createDate: response.createDate || '',
            created_at: response.createDate || '',
            deviceCount: response.deviceCount || 0,
            bound_device_ids: response.bound_device_ids || [],
            role: response.role || 'user',
            roleId: response.roleId,
            groupName: response.groupName || '',
            groupIdList: response.groupIdList || response.groupIds || [],
            groupIds: response.groupIds || response.groupIdList || [],
            avatar: response.avatar || 'default:1'
          }
          commit('UPDATE_USER', user)
          return Promise.resolve(user)
        }
        return Promise.reject(new Error('User not found'))
      } catch (error) {
        silentError('获取用户详情失败:', error)
        throw error
      }
    },
    
    // 用户管理相关actions
    async addUser({ commit, dispatch, state }, userData) {
      try {
        // 调用创建用户API
        const response = await import('@/apis/user/user.js').then(m => m.createUser(userData))
        
        // 只要没有抛出异常（request.js 已经处理了 code !== 0），就认为是成功
        if (response !== undefined) {
          const newUser = {
            userid: (response && typeof response === 'object') ? (response.userId || response.userid || response.id || Date.now()) : (response || Date.now()),
            username: userData.username,
            email: userData.email,
            name: userData.name || userData.username,
            status: userData.status !== undefined ? userData.status : 1,
            role: userData.role || 'user',
            roleId: userData.roleId,
            created_at: new Date().toISOString()
          }
          
          commit('ADD_USER', newUser)
          
          // 记录用户创建日志
          dispatch('addSystemLog', {
            type: 'user_operation',
            content: `创建用户: ${userData.username}`,
            user_id: state.auth.user ? state.auth.user.userid : null,
            ip: '127.0.0.1',
            created_at: new Date().toISOString()
          })
          
          return Promise.resolve(newUser)
        } else {
          throw new Error('创建用户失败: 响应为空')
        }
      } catch (error) {
        silentError('创建用户失败:', error)
        throw error
      }
    },
    
    async updateUser({ commit, dispatch, state }, userData) {
      try {
        const response = await import('@/apis/user/user.js').then(m => m.updateUser(userData.userid, userData))
        
        if (response !== undefined) {
          await dispatch('fetchUserById', userData.userid)
          
          dispatch('addSystemLog', {
            type: 'user_operation',
            content: `更新用户: ${userData.username}`,
            user_id: state.auth.user ? state.auth.user.userid : null,
            ip: '127.0.0.1',
            created_at: new Date().toISOString()
          })
          
          const updatedUser = state.users.find(u => String(u.userid) === String(userData.userid))
          return Promise.resolve(updatedUser || userData)
        } else {
          throw new Error('更新用户失败: 响应为空')
        }
      } catch (error) {
        silentError('更新用户失败:', error)
        throw error
      }
    },
    
    async deleteUser({ commit, dispatch, state }, userId) {
      try {
        // 调用删除用户API
        const response = await import('@/apis/user/user.js').then(m => m.deleteUser(userId))
        
        // 只要没有抛出异常，就认为是成功
        if (response !== undefined) {
          commit('DELETE_USER', userId)
          
          // 记录用户删除日志
          dispatch('addSystemLog', {
            type: 'user_operation',
            content: `注销用户ID: ${userId}`,
            user_id: state.auth.user ? state.auth.user.userid : null,
            ip: '127.0.0.1',
            created_at: new Date().toISOString()
          })
          
          return Promise.resolve(userId)
        } else {
          throw new Error('注销用户失败: 响应为空')
        }
      } catch (error) {
        silentError('注销用户失败:', error)
        throw error
      }
    },
    
    async toggleUserStatus({ commit, state, dispatch }, userId) {
      try {
        let user = state.users.find(u => String(u.userid) === String(userId))
        
        // 如果在全局用户列表中找不到，则尝试在分组列表中寻找
        if (!user) {
          state.userGroups.forEach(group => {
            if (group.users) {
              const groupUser = group.users.find(u => String(u.userid || u.userId || u.id) === String(userId))
              if (groupUser) user = groupUser
            }
          })
        }
        
        if (!user) throw new Error('用户不存在')
        
        const newStatus = user.status === 1 ? 0 : 1
        return dispatch('setUserStatus', { userId, status: newStatus, username: user.username })
      } catch (error) {
        silentError('切换用户状态失败:', error)
        throw error
      }
    },
    
    async setUserStatus({ commit, state, dispatch }, { userId, status, username }) {
      try {
        const user = state.users.find(u => String(u.userid) === String(userId))
        // 如果在全局用户列表中找不到，则尝试在分组列表中寻找
        let foundUsername = username
        if (!foundUsername) {
          if (user) {
            foundUsername = user.username
          } else {
            // 尝试在分组详情中寻找
            state.userGroups.forEach(group => {
              if (group.users) {
                const groupUser = group.users.find(u => String(u.userid || u.userId || u.id) === String(userId))
                if (groupUser) foundUsername = groupUser.username
              }
            })
          }
        }
        
        const response = await import('@/apis/user/user.js').then(m => m.updateUserStatus(userId, status))
        
        // 只要没有抛出异常，就认为是成功
        if (response !== undefined) {
          commit('SET_USER_STATUS', { userId, status })
          
          // 记录状态变更日志
          dispatch('addSystemLog', {
            type: 'user_operation',
            content: `修改用户状态: ${foundUsername || userId}, 新状态: ${status === 1 ? '启用' : '禁用'}`,
            user_id: state.auth.user ? state.auth.user.userid : null,
            ip: '127.0.0.1',
            created_at: new Date().toISOString()
          })
          
          return Promise.resolve(userId)
        } else {
          throw new Error('修改用户状态失败: 响应为空')
        }
      } catch (error) {
        silentError('修改用户状态失败:', error)
        throw error
      }
    },
    
    async batchDeleteUsers({ commit, dispatch }, ids) {
      try {
        const response = await import('@/apis/user/user.js').then(m => m.batchDeleteUsers(ids))
        if (response !== undefined) {
          ids.forEach(id => commit('DELETE_USER', id))
          return Promise.resolve(response)
        } else {
          throw new Error('批量注销用户失败: 响应为空')
        }
      } catch (error) {
        silentError('批量注销用户失败:', error)
        throw error
      }
    },

    async batchUpdateUserStatus({ commit }, { ids, status }) {
      try {
        const response = await import('@/apis/user/user.js').then(m => m.batchUpdateUserStatus(ids, status))
        if (response !== undefined) {
          // Update local state
          ids.forEach(id => {
            commit('SET_USER_STATUS', { userId: id, status })
          })
          return Promise.resolve(response)
        } else {
          throw new Error('批量更新用户状态失败: 响应为空')
        }
      } catch (error) {
        silentError('批量更新用户状态失败:', error)
        throw error
      }
    },
    
    async fetchUserGroups({ commit }, params) {
      try {
        const response = await import('@/apis/user/userGroup.js').then(m => m.getUserGroups(params))
        if (response !== undefined) {
          const data = response.data || response
          let groups = []
          if (Array.isArray(data)) {
            groups = data
          } else if (data && data.list) {
            groups = data.list
          } else if (data && data.records) {
            groups = data.records
          }
          commit('SET_USER_GROUPS', groups)
          return Promise.resolve(groups)
        } else {
          commit('SET_USER_GROUPS', [])
          return Promise.resolve([])
        }
      } catch (error) {
        silentError('获取用户组列表失败:', error)
        commit('SET_USER_GROUPS', [])
        throw error
      }
    },

    async fetchUserGroup({ commit, state }, id) {
      try {
        const response = await import('@/apis/user/userGroup.js').then(m => m.getUserGroupDetail(id))
        if (response !== undefined) {
          const group = response.data || response
          const exists = state.userGroups.find(g => String(g.id) === String(group.id))
          if (exists) {
            commit('UPDATE_USER_GROUP', group)
          } else {
            commit('ADD_USER_GROUP', group)
          }
          return Promise.resolve(group)
        } else {
          throw new Error('获取用户组详情失败')
        }
      } catch (error) {
        silentError('获取用户组详情失败:', error)
        throw error
      }
    },
    
    async createUserGroup({ commit }, group) {
      try {
        const response = await import('@/apis/user/userGroup.js').then(m => m.createUserGroup(group))
        if (response !== undefined) {
          return Promise.resolve(response)
        } else {
          throw new Error('创建用户组失败')
        }
      } catch (error) {
        silentError('创建用户组失败:', error)
        throw error
      }
    },
    
    async updateUserGroup({ commit, state }, group) {
      try {
        const response = await import('@/apis/user/userGroup.js').then(m => m.updateUserGroup(group))
        if (response !== undefined) {
          // 获取更新后的完整组信息，或者手动更新本地状态
          // 由于 API 返回可能不包含完整信息，我们先尝试更新本地状态
          const index = state.userGroups.findIndex(g => String(g.id) === String(group.id))
          if (index !== -1) {
            const updatedGroup = { ...state.userGroups[index], ...group }
            // 如果 response 包含最新数据，则使用 response
            commit('UPDATE_USER_GROUP', response && response.id ? response : updatedGroup)
          }
          return Promise.resolve(response)
        } else {
          throw new Error('更新用户组失败')
        }
      } catch (error) {
        silentError('更新用户组失败:', error)
        throw error
      }
    },
    
    async deleteUserGroup({ commit }, ids) {
      try {
        const response = await import('@/apis/user/userGroup.js').then(m => m.deleteUserGroup(ids))
        // response 可能是 undefined (如果后端返回 void 且 axios 拦截器直接返回了 data)
        // 或者 response.code === 0
        if (response === undefined || response === null || (response && response.code === 0)) {
          return Promise.resolve(response)
        } else {
          throw new Error('删除用户组失败')
        }
      } catch (error) {
        silentError('删除用户组失败:', error)
        throw error
      }
    },

    // 硬件设备管理相关actions
    async fetchDevices({ commit, state }, params = {}) {
      try {
        const { forceRefresh = false, ...apiParams } = params
        
        // 缓存机制：如果已有数据且非强制刷新，则直接返回
        if (!forceRefresh && state.devices && state.devices.length > 0 && Object.keys(apiParams).length === 0) {
          if(process.env.NODE_ENV==="development")console.debug('Using cached devices data')
          return Promise.resolve(state.devices)
        }

        const response = await import('@/apis/device/device.js').then(m => m.getDeviceList(apiParams))
        if (response !== undefined) {
          const data = response.data || response
          let backendDevices = []
          if (Array.isArray(data)) {
            backendDevices = data
          } else if (data && (data.list || data.records)) {
            backendDevices = data.list || data.records
          }
          
          // 字段映射：将后端驼峰命名映射为前端期望的下划线命名
          const devices = backendDevices.map(d => ({
            ...d,
            user_id: d.userId || d.userid || d.user_id || null,
            mac_address: d.macAddress || d.mac_address || '',
            device_name: d.deviceName || d.device_name || '',
            device_code: d.deviceCode || d.device_code || '',
            device_remark: d.deviceRemark || d.device_remark || '',
            app_version: d.appVersion || d.app_version || '',
            auto_update: d.autoUpdate !== undefined ? d.autoUpdate : (d.auto_update !== undefined ? d.auto_update : 0),
            created_at: d.createDate || d.create_at || d.created_at || ''
          }))
          
          commit('SET_DEVICES', devices)
          return Promise.resolve(devices)
        } else {
          commit('SET_DEVICES', [])
          return Promise.resolve([])
        }
      } catch (error) {
        silentError('获取设备列表失败:', error)
        commit('SET_DEVICES', [])
        throw error
      }
    },

    addDevice({ commit }, device) {
      // 模拟API调用
      const newDevice = { ...device, id: Date.now() }
      commit('ADD_DEVICE', newDevice)
      return Promise.resolve(newDevice)
    },
    updateDevice({ commit }, updatedDevice) {
      // 模拟API调用
      commit('UPDATE_DEVICE', updatedDevice)
      return Promise.resolve(updatedDevice)
    },
    deleteDevice({ commit }, deviceId) {
      // 模拟API调用
      commit('DELETE_DEVICE', deviceId)
      return Promise.resolve(deviceId)
    },
    bindDevice({ commit }, { deviceId, userId }) {
      // 模拟API调用
      commit('BIND_DEVICE', { deviceId, userId })
      // 添加操作日志
      commit('ADD_DEVICE_LOG', {
        id: Date.now(),
        device_id: deviceId,
        operation_type: 'device_bind',
        operation_content: `将设备绑定到用户ID: ${userId}`,
        user_id: userId,
        created_at: new Date().toISOString()
      })
      return Promise.resolve({ deviceId, userId })
    },
    async unbindDevice({ commit, dispatch, state }, { deviceId, userId }) {
      try {
        const response = await import('@/apis/device/device.js').then(m => m.unbindDevice(deviceId, userId))
        if (response !== undefined) {
          commit('UNBIND_DEVICE', deviceId)
          // 强制刷新设备列表以同步最新状态
          await dispatch('fetchDevices', { forceRefresh: true })
          
          // 添加操作日志
          commit('ADD_DEVICE_LOG', {
            id: Date.now(),
            device_id: deviceId,
            operation_type: 'device_unbind',
            operation_content: '解绑设备',
            user_id: state.auth.user ? state.auth.user.id : null,
            created_at: new Date().toISOString()
          })
          
          return Promise.resolve(response)
        } else {
          throw new Error('解绑设备失败')
        }
      } catch (error) {
        silentError('解绑设备失败:', error)
        throw error
      }
    },
    
    // 系统管理相关actions
    addSystemLog({ commit }, log) {
      // 模拟API调用
      const newLog = { ...log, id: Date.now() }
      commit('ADD_SYSTEM_LOG', newLog)
      return Promise.resolve(newLog)
    },
    addNotificationTemplate({ commit }, template) {
      // 模拟API调用
      const newTemplate = { ...template, id: Date.now() }
      commit('ADD_NOTIFICATION_TEMPLATE', newTemplate)
      return Promise.resolve(newTemplate)
    },
    updateNotificationTemplate({ commit }, updatedTemplate) {
      // 模拟API调用
      commit('UPDATE_NOTIFICATION_TEMPLATE', updatedTemplate)
      return Promise.resolve(updatedTemplate)
    },
    deleteNotificationTemplate({ commit }, templateId) {
      // 模拟API调用
      commit('DELETE_NOTIFICATION_TEMPLATE', templateId)
      return Promise.resolve(templateId)
    },
    toggleNotificationTemplate({ commit }, templateId) {
      // 模拟API调用
      commit('TOGGLE_NOTIFICATION_TEMPLATE', templateId)
      return Promise.resolve(templateId)
    },
    
    // 密码管理相关actions
    async forgotPassword({ commit, dispatch }, { email, captcha, captchaId }) {
      try {
        // 调用发送重置密码邮件API
        const response = await import('@/apis/auth/auth.js').then(m => m.sendResetPasswordEmail({
          email,
          captcha,
          captchaId
        }))
        
        if (response) {
          // 记录密码找回请求日志
          dispatch('addSystemLog', {
            type: 'security',
            content: `密码找回请求: ${email}`,
            ip: '127.0.0.1',
            created_at: new Date().toISOString()
          })
          
          return Promise.resolve(response)
        } else {
          throw new Error('密码找回响应格式错误')
        }
      } catch (error) {
        silentError('密码找回失败:', error)
        throw error
      }
    },
    
    async resetPassword({ commit, dispatch }, { token, newPassword }) {
      try {
        // 调用密码重置API
        const response = await import('@/apis/auth/auth.js').then(m => m.resetPassword({
          token,
          newPassword
        }))
        
        if (response) {
          // 记录密码重置日志
          dispatch('addSystemLog', {
            type: 'security',
            content: `密码重置成功`,
            ip: '127.0.0.1',
            created_at: new Date().toISOString()
          })
          
          return Promise.resolve(response)
        } else {
          throw new Error('密码重置响应格式错误')
        }
      } catch (error) {
        silentError('密码重置失败:', error)
        throw error
      }
    },
    
    async changePassword({ commit, dispatch, state }, { oldPassword, newPassword, captcha, captchaId }) {
      try {
        // 调用密码修改API
        const response = await import('@/apis/auth/auth.js').then(m => m.changePassword({
          oldPassword,
          newPassword,
          captcha,
          captchaId
        }))
        
        if (response) {
          // 记录密码修改日志
          dispatch('addSystemLog', {
            type: 'security',
            content: `用户修改密码`,
            user_id: state.auth.user ? state.auth.user.id : null,
            ip: '127.0.0.1',
            created_at: new Date().toISOString()
          })
          
          return Promise.resolve(response)
        } else {
          throw new Error('密码修改响应格式错误')
        }
      } catch (error) {
        silentError('密码修改失败:', error)
        throw error
      }
    }
  }
})